import PropTypes from 'prop-types';
import { createContext, useContext, useState } from 'react';

export const WorkReportModalsContext = createContext();

const initialShowModal = {
  applyTemplateOptions: false,
  approveAllModal: false,
  attachFileModal: false,
  commentsModal: false,
  confirmApprovalModal: false,
  editQuantityModal: false,
  endServiceModal: false,
  expertReviewModal: false,
  modifyImputationModal: false,
  modifyNewValueImputationModal: false,
  inspectorExpertReviewModal: false,
  monthlySummaryModal: false,
  quantityEntryModal: false,
  rejectModal: false,
  templateWorkReportModal: false,
  totalLineModal: false,
  statusReferenceModal: false,
  approveOrRejectSelectedQuantitiesModal: false,
  programPointModal: false,
  imputationObjectModal: false,
  programPointApprovalModal: false
};

export const useWorkReportModals = () => useContext(WorkReportModalsContext);

const WorkReportModalsProvider = ({ children }) => {
  const [showModal, setShowModal] = useState(initialShowModal);
  const [props, setProps] = useState({});
  const [selectedDate, setSelectedDate] = useState('');

  const activeModal = (modal) => {
    setShowModal((state) => ({ ...state, [modal]: true }));
  };
  const hideModal = () => {
    setShowModal(initialShowModal);
  };

  const activeTotalLineModal = (idLine, monthly) => {
    activeModal('totalLineModal');
    setProps({
      idLine,
      monthly
    });
  };

  const activeCommentsModal = (dailyWorkReport, onAccept, disabled) => {
    activeModal('commentsModal');
    setProps({
      positionName: dailyWorkReport?.positionName || dailyWorkReport?.posicionSap,
      lineName: dailyWorkReport?.lineName || dailyWorkReport?.lineaSap,
      date: selectedDate || dailyWorkReport?.startDate || dailyWorkReport?.date,
      dailyWorkReportId: dailyWorkReport?.idParteDiario,
      um: dailyWorkReport?.unidadMedida,
      quantity: dailyWorkReport?.cantidad,
      monthly: dailyWorkReport?.monthly,
      onAccept,
      disabled
    });
  };

  const activeAttachFileModal = (dailyWorkReport, onAccept, disableEdit) => {
    activeModal('attachFileModal');
    setProps({
      positionName: dailyWorkReport?.positionName || dailyWorkReport?.posicionSap,
      lineName: dailyWorkReport?.lineName || dailyWorkReport?.lineaSap,
      date: selectedDate || dailyWorkReport?.startDate || dailyWorkReport?.date,
      dailyWorkReportId: dailyWorkReport?.idParteDiario,
      um: dailyWorkReport?.unidadMedida,
      quantity: dailyWorkReport?.cantidad,
      monthly: dailyWorkReport?.monthly,
      type: dailyWorkReport?.tipoAsignacion,
      requestId: dailyWorkReport?.idSolicitud,
      onAccept,
      disableEdit
    });
  };

  const activeQuantityEntryModal = (dailyWorkReport, onAccept) => {
    activeModal('quantityEntryModal');
    setProps({
      workReportId:
        dailyWorkReport?.idPartetrabajo || dailyWorkReport?.idParteTrabajoUbicacionLinea,
      dailyWorkReportId: dailyWorkReport?.idParteDiario,
      positionName: dailyWorkReport?.positionName,
      lineName: dailyWorkReport?.lineName,
      date: selectedDate || dailyWorkReport?.startDate,
      currentQuantity: dailyWorkReport?.cantidad,
      onAccept
    });
  };

  const activeMonthlySummaryModal = ({ dailyWorkReport, monthly, filesPermissions }) => {
    activeModal('monthlySummaryModal');
    setProps({
      line: dailyWorkReport?.idParteLinea || dailyWorkReport?.idParteTrabajoUbicacionLinea,
      date: selectedDate || dailyWorkReport?.date,
      idWork: dailyWorkReport?.idParteTrabajo,
      tipoAsignacion: dailyWorkReport?.tipoAsignacion,
      monthly,
      filesPermissions
    });
  };

  const activeRejectModal = (dailyWorkReport, onAccept) => {
    activeModal('rejectModal');
    setProps({
      unidadMedida: dailyWorkReport?.unidadMedida,
      tienePuntosPrograma: dailyWorkReport?.tienePuntosPrograma,
      dailyWorkReportId: dailyWorkReport?.idParteDiario,
      positionName: dailyWorkReport?.posicionSap,
      lineName: dailyWorkReport?.lineaSap,
      asignationType: dailyWorkReport?.tipoAsignacion,
      date: selectedDate || dailyWorkReport?.date,
      um: dailyWorkReport?.unidadMedida,
      quantity: dailyWorkReport?.cantidad,
      tipoUbicacionEnum: dailyWorkReport?.tipoUbicacionEnum,
      notificaPorPuntosPrograma: dailyWorkReport?.notificaPorPuntosPrograma,
      notificadaPorPuntoDePrograma: dailyWorkReport?.notificadaPorPuntoDePrograma,
      variosPuntosPrograma: dailyWorkReport?.variosPuntosPrograma,
      puntoPrograma: dailyWorkReport?.puntoPrograma,
      puedeNotificarPorPuntoPrograma: dailyWorkReport?.puedeNotificarPorPuntoPrograma,
      defaultValue: dailyWorkReport?.defaultValue,
      numeroContrato: dailyWorkReport?.numeroContrato,
      variosValoresOperativos: dailyWorkReport?.variosValoresOperativos,
      codigoOperativo: dailyWorkReport?.codigoOperativo,
      onAccept
    });
  };

  const activeApproveAllModal = (allIds, onAccept) => {
    activeModal('approveAllModal');
    setProps({
      allIds,
      onAccept
    });
  };

  const activeExpertReviewModal = (dailyWorkReport, onAccept) => {
    activeModal('expertReviewModal');
    setProps({
      dailyWorkReportId: dailyWorkReport?.idParteDiario,
      positionName: dailyWorkReport?.posicionSap,
      lineName: dailyWorkReport?.lineaSap,
      date: selectedDate || dailyWorkReport?.date,
      um: dailyWorkReport?.unidadMedida,
      quantity: dailyWorkReport?.cantidad,
      assignmentTypeEnum: dailyWorkReport?.tipoAsignacion,
      onAccept
    });
  };

  const activeInspectorExpertReviewModal = (
    dailyWorkReport,
    onAccept,
    asignationType,
    setNewFetch
  ) => {
    activeModal('inspectorExpertReviewModal');
    setProps({
      dailyWorkReportId: dailyWorkReport?.idParteDiario || dailyWorkReport?.id,
      positionName: dailyWorkReport?.positionName || dailyWorkReport?.descripcionPosicionSap,
      lineName: dailyWorkReport?.lineName || dailyWorkReport?.descripcionLineaSap,
      date: selectedDate || dailyWorkReport?.date,
      um: dailyWorkReport?.unidadMedida,
      quantity: dailyWorkReport?.cantidad,
      assignmentTypeEnum: dailyWorkReport?.tipoAsignacion,
      line: dailyWorkReport,
      onAccept,
      asignationType,
      setNewFetch
    });
  };

  const activeEditQuantityModal = (dailyWorkReport, onAccept) => {
    activeModal('editQuantityModal');
    setProps({
      workReportId:
        dailyWorkReport?.idParteTrabajo || dailyWorkReport?.idParteTrabajoUbicacionLinea,
      dailyWorkReportId: dailyWorkReport?.idParteDiario,
      positionName: dailyWorkReport?.posicionSap,
      lineName: dailyWorkReport?.lineaSap,
      date: selectedDate || dailyWorkReport?.date,
      um: dailyWorkReport?.unidadMedida,
      quantity: dailyWorkReport?.cantidad,
      onAccept,
      yClickCertification: dailyWorkReport?.certificaYclick,
      estadoEnum: dailyWorkReport?.estadoEnum,
      tienePuntosPrograma: dailyWorkReport?.tienePuntosPrograma,
      tipoUbicacionEnum: dailyWorkReport?.tipoUbicacionEnum,
      notificaPorPuntosPrograma:
        dailyWorkReport?.notificaPorPuntosPrograma || dailyWorkReport?.notificadaPorPuntoDePrograma,
      puedeNotificarPorPuntoPrograma: dailyWorkReport?.puedeNotificarPorPuntoPrograma,
      variosPuntosPrograma: dailyWorkReport?.variosPuntosPrograma,
      puntoPrograma: dailyWorkReport?.puntoPrograma,
      viewCSA: true,
      editCSA: true,
      tipoAsignacion: dailyWorkReport?.tipoAsignacion,
      defaultValue: dailyWorkReport?.defaultValue,
      numeroContrato: dailyWorkReport?.numeroContrato,
      variosValoresOperativos: dailyWorkReport?.variosValoresOperativos,
      codigoOperativo: dailyWorkReport?.codigoOperativo
    });
  };

  const activeConfirmApprovalModal = (dailyWorkReport, onAccept) => {
    activeModal('confirmApprovalModal');
    setProps({
      dailyWorkReportId: dailyWorkReport?.idParteDiario,
      onAccept
    });
  };

  const activeEndServiceModal = (info, tipoAsignacion) => {
    activeModal('endServiceModal');
    setProps({
      lineInfo: info,
      tipoAsignacion
    });
  };

  // Modal plantillas SDS-ASDS
  const activeTemplateWorkReportModal = ({
    onAccept,
    monthly = false,
    setShowAddUbicationsToPLModal,
    setApplyTemplateView,
    setTemplateId,
    applied,
    contractNumber,
    // La solicitud tiene lineas ya asignadas?
    hasLines,
    disabledEditStateCancelOrFinish,
    showOnCallRows = true,
    showMonthlyRows = true,
    endingDate,
    initialDate
  }) => {
    activeModal('templateWorkReportModal');
    setProps({
      onAccept,
      monthly,
      setShowAddUbicationsToPLModal,
      setApplyTemplateView,
      setTemplateId,
      applied,
      contractNumber,
      hasLines,
      disabledEditStateCancelOrFinish,
      showOnCallRows,
      showMonthlyRows,
      endingDate,
      initialDate
    });
  };

  const activeModifyImputation = ({ dailyWorkReport, onAccept }) => {
    activeModal('modifyImputationModal');
    setProps({ dailyWorkReport, onAccept });
  };

  const activeNewValueImputation = ({ onAccept }) => {
    activeModal('modifyNewValueImputationModal');
    setProps({ onAccept });
  };

  const activeStatusReference = () => {
    activeModal('statusReferenceModal');
    setProps({});
  };

  const activeApproveSelectedQuantitiesModal = ({
    idsPartesDiario,
    agrupaPorUbicacion,
    handleApprove
  }) => {
    const modalTitle = 'Confirmar autorización';
    const labelPrimaryButton = 'Confirmar todas las autorizaciones';

    activeModal('approveOrRejectSelectedQuantitiesModal');
    setProps({
      idsPartesDiario,
      agrupaPorUbicacion,
      handlePrimaryAction: handleApprove,
      modalTitle,
      labelPrimaryButton
    });
  };

  const activeRejectSelectedQuantitiesModal = ({
    idsPartesDiario,
    agrupaPorUbicacion,
    handleReject
  }) => {
    const modalTitle = 'Confirmar rechazo';
    const labelPrimaryButton = 'Confirmar todos los rechazos';

    activeModal('approveOrRejectSelectedQuantitiesModal');
    setProps({
      idsPartesDiario,
      agrupaPorUbicacion,
      handlePrimaryAction: handleReject,
      modalTitle,
      labelPrimaryButton
    });
  };

  const activeProgramPointModal = ({
    line,
    selectedDate: date,
    amount,
    setAmount,
    disabledPPModal,
    tienePuntosPrograma,
    setIsRectifyAmount,
    isRectifyAmount,
    setNewFetch
  }) => {
    activeModal('programPointModal');
    setProps({
      line,
      selectedDate: date,
      amount,
      setAmount,
      disabledPPModal,
      tienePuntosPrograma,
      setIsRectifyAmount,
      isRectifyAmount,
      setNewFetch
    });
  };
  const activeImputationObjectModal = ({
    imputationObject,
    requestNumber,
    titleUbication,
    monthlyRequest,
    dateEnd,
    dateInit,
    idSolicitud,
    proveedor,
    idUbicacion
  }) => {
    activeModal('imputationObjectModal');
    setProps({
      imputationObject,
      requestNumber,
      titleUbication,
      monthlyRequest,
      dateEnd,
      dateInit,
      idSolicitud,
      proveedor,
      idUbicacion
    });
  };

  const activeProgramPointApprovalModal = ({
    daily,
    fetchData,
    handleChangedActions,
    dateSelect,
    onAccept,
    line,
    viewCSA,
    editCSA,
    defaultValue
  }) => {
    activeModal('programPointApprovalModal');
    setProps({
      daily,
      line,
      fetchData,
      handleChangedActions,
      dateSelect,
      viewCSA,
      editCSA,
      onAccept,
      defaultValue
    });
  };

  return (
    <WorkReportModalsContext.Provider
      value={{
        activeApproveAllModal,
        activeApproveSelectedQuantitiesModal,
        activeAttachFileModal,
        activeCommentsModal,
        activeConfirmApprovalModal,
        activeEditQuantityModal,
        activeEndServiceModal,
        activeExpertReviewModal,
        activeInspectorExpertReviewModal,
        activeModifyImputation,
        activeNewValueImputation,
        activeMonthlySummaryModal,
        activeQuantityEntryModal,
        activeRejectModal,
        activeTemplateWorkReportModal,
        activeTotalLineModal,
        activeStatusReference,
        activeRejectSelectedQuantitiesModal,
        activeProgramPointModal,
        activeImputationObjectModal,
        activeProgramPointApprovalModal,
        hideModal,
        props,
        setSelectedDate,
        showModal
      }}
    >
      {children}
    </WorkReportModalsContext.Provider>
  );
};

WorkReportModalsProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export default WorkReportModalsProvider;
